import React, { useState, useEffect } from 'react';
import { settingsApi, formatApiError } from '../services/api';
import { Camera, Plus, Trash, ArrowsDownUp } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const PhotoSlotsCard = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await settingsApi.getPhotoSlots();
      setSlots(res.data?.slots || []);
    } catch (err) {
      toast.error(formatApiError(err) || 'Foto-Slots konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const update = (idx, patch) => {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addSlot = () => {
    const key = `custom_${Date.now()}`;
    setSlots((prev) => [...prev, { key, label: 'Neuer Slot', required: false, active: true }]);
  };

  const remove = (idx) => {
    if (!window.confirm('Slot wirklich entfernen?')) return;
    setSlots((prev) => prev.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    setSlots((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      const res = await settingsApi.updatePhotoSlots(slots);
      setSlots(res.data?.slots || []);
      toast.success('Foto-Slots gespeichert');
    } catch (err) {
      toast.error(formatApiError(err) || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card-swiss p-6">
        <p className="text-sm text-[#525866]">Lade Foto-Slots…</p>
      </div>
    );
  }

  return (
    <div className="card-swiss p-6" data-testid="photo-slots-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
          <Camera weight="fill" size={20} className="text-[#E60000]" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold">Foto-Slots bei Übergabe / Rückgabe</h2>
          <p className="text-sm text-[#525866]">
            Was muss bei Übergabe (Check-In) und Rückgabe (Check-Out) fotografiert werden?
            Pflicht-Slots zeigen sich als rote Kacheln im Übergabe-Dialog.
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[#525866] border-b border-[#E2E4E9]">
            <tr>
              <th className="py-2 pr-2 w-12"></th>
              <th className="py-2 pr-2">Schlüssel</th>
              <th className="py-2 pr-2">Beschriftung</th>
              <th className="py-2 pr-2 text-center w-20">Pflicht</th>
              <th className="py-2 pr-2 text-center w-20">Aktiv</th>
              <th className="py-2 pr-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {slots.map((s, idx) => (
              <tr key={`${s.key}-${idx}`} className="border-b border-[#E2E4E9] hover:bg-[#FAFAFB]">
                <td className="py-2 pr-2">
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      className="text-[#525866] hover:text-[#111418] disabled:opacity-30"
                      disabled={idx === 0}
                      onClick={() => move(idx, -1)}
                      aria-label="Nach oben"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="text-[#525866] hover:text-[#111418] disabled:opacity-30"
                      disabled={idx === slots.length - 1}
                      onClick={() => move(idx, 1)}
                      aria-label="Nach unten"
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td className="py-2 pr-2">
                  <Input
                    value={s.key}
                    onChange={(e) => update(idx, { key: e.target.value })}
                    className="input-swiss font-mono text-xs"
                    data-testid={`photo-slot-key-${idx}`}
                  />
                </td>
                <td className="py-2 pr-2">
                  <Input
                    value={s.label}
                    onChange={(e) => update(idx, { label: e.target.value })}
                    className="input-swiss"
                    data-testid={`photo-slot-label-${idx}`}
                  />
                </td>
                <td className="py-2 pr-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!s.required}
                    onChange={(e) => update(idx, { required: e.target.checked })}
                    className="w-4 h-4 accent-[#E60000]"
                    data-testid={`photo-slot-required-${idx}`}
                  />
                </td>
                <td className="py-2 pr-2 text-center">
                  <input
                    type="checkbox"
                    checked={s.active !== false}
                    onChange={(e) => update(idx, { active: e.target.checked })}
                    className="w-4 h-4 accent-[#009951]"
                    data-testid={`photo-slot-active-${idx}`}
                  />
                </td>
                <td className="py-2 pr-2 text-right">
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="text-[#525866] hover:text-[#E60000]"
                    aria-label="Entfernen"
                  >
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {slots.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-[#8A94A6]">
                  Keine Slots definiert.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button type="button" variant="outline" size="sm" onClick={addSlot} data-testid="photo-slot-add-btn">
          <Plus size={14} className="mr-1" /> Slot hinzufügen
        </Button>
        <Button type="button" onClick={save} disabled={saving} className="btn-primary" data-testid="photo-slot-save-btn">
          {saving ? 'Speichere…' : 'Slots speichern'}
        </Button>
      </div>

      <p className="text-xs text-[#8A94A6] mt-3">
        Hinweis: Der Schlüssel <code className="font-mono">extra</code> wird im Übergabe-Dialog als
        Mehrfach-Slot („Zusatzfotos") gerendert. Pflicht-Slots werden im Dialog rot markiert; das
        Abschließen ist trotz fehlender Pflicht-Fotos möglich (Warnung wird angezeigt).
      </p>
    </div>
  );
};

export default PhotoSlotsCard;
